import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { intersection } from 'underscore';
import {
  CreateCourseCertificate,
  CreateCourseRequirement,
  DeleteCourse,
  DeleteCourseRequirement,
  GetChannelTracks,
  GetCourseForEvent,
  UpdateCourse,
  UpdateCourseCertificate,
  UpdateCourseRequirement,
} from '@/connection/courses';
import {
  CourseRequirementType,
  ICourse,
  ICourseCertificate,
  ICourseRequirement,
  ICreateCourseCertificate,
  ICreateCourseRequirement,
} from '@/types/courses';
import EditDesign from './components/admin-certificate/edit-design-modal';
import { createNewCertificate } from './components/admin-certificate/new-certificate-helper';
import EmailPreview from './components/admin-certificate/email-preview';
import CertificatePanel from './components/admin-certificate/certificate-panel';
import NoCourse from './components/no-course-view';
import CourseHeader from './components/course-header';
import { defaultNewRequirement, getBannerImage } from './utils/course-utils';
import CourseMainPanel from './components/course-main-panel';
import CourseAddRequirementModal from './components/course-add-requirement-modal';
import CourseDeleteRequirementModal from './components/course-delete-requirement-modal';
import CourseDeleteModal from './components/course-delete-modal';
import CourseNoCertNavAlertModal from './components/course-no-cert-nav-alert-modal';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import { getWorkingProject } from '@/store/slices/project/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { EPermissions } from '@/types/permissions.types';
import { getChannels } from '@/store/slices/channels/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import { FetchError } from '@/connection/helpers';
import Tabs from '@ui/tabs/tabs';
import { useBlocker, Location } from 'react-router-dom';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import BrandedLoadingIndicator from '@/components/branding/branded-loading-indicator';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

interface BlockNavParams {
  currentLocation: Location;
  nextLocation: Location;
}

const Courses = () => {
  const user = useAppSelector(getUser);
  const workingEvent = useAppSelector(getWorkingProject);
  const channels = useAppSelector(getChannels);
  const channel = channels?.find((channel) => channel.channel === user?.active_channel);

  const [course, setCourse] = useState<ICourse | null>(null);
  const [courseLoading, setCourseLoading] = useState(true);
  const [courseFetchError, setCourseFetchError] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('Course');
  const [newRequirement, setNewRequirement] = useState(defaultNewRequirement);
  const [savingRequirement, setSavingRequirement] = useState(false);
  const [showAddNewRequirementModal, setShowAddNewRequirementModal] = useState(false);
  const [showAddRequirementPopover, setShowAddRequirementPopover] = useState(false);
  const [requirementToDelete, setRequirementToDelete] = useState<string | null>(null);
  const [requirementToUpdate, setRequirementToUpdate] = useState<string | null>(null);
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [editDesignModalOpen, setEditDesignModalOpen] = useState(false);
  const [workingCertificate, setWorkingCertificate] = useState<
    ICourseCertificate | ICreateCourseCertificate | undefined
  >(undefined);
  const [savingCertificate, setSavingCertificate] = useState(false);
  const [showDeleteCourseModal, setShowDeleteCourseModal] = useState(false);
  const [hasVisitedCertificateTab, setHasVisitedCertificateTab] = useState(false);
  const [showCertificateWarningModal, setShowCertificateWarningModal] = useState(false);

  const COURSE_TABS: { label: string; value: string }[] = [
    { label: 'Course', value: 'Course' },
    { label: 'Certificate', value: 'Certificate' },
  ];

  const blockNav = useCallback(
    ({ currentLocation, nextLocation }: BlockNavParams) => {
      if (course && !course?.certificate && currentLocation.pathname !== nextLocation.pathname) {
        setShowCertificateWarningModal(true);
        return true;
      }
      return false;
    },
    [course]
  );

  const blocker = useBlocker(blockNav);

  const isEditorRole = !!(
    user &&
    intersection(user.channels[user.active_channel], [EPermissions.Editor]).length &&
    !intersection(user.channels[user.active_channel], [EPermissions.Admin]).length
  );

  const sessionHasStarted = useCallback(
    (requirement: ICourseRequirement | ICreateCourseRequirement) => {
      const session = workingEvent?.sessions.find((session) => session.session === requirement?.session_id);

      if (!session) return false;

      return !!(session.timestamp && session.timestamp < Date.now());
    },
    [workingEvent?.sessions]
  );

  const handleSave = useCallback(
    async (name?: string, description?: string, categories?: string[], credits?: number) => {
      if (!user?.active_channel || !course) return;
      try {
        const newCourse = {
          ...course,
          ...(name && { name: { ...course.name, base: name } }),
          ...(description && { description }),
          ...(categories && { categories }),
          ...(credits && { credits }),
        };

        await UpdateCourse(user.active_channel, course.uuid, newCourse);
        setCourse(newCourse);
      } catch (e) {
        console.error(e);
        toastTrigger(<div className="S1">Error updating course</div>, {
          type: 'error',
          autoClose: 5000,
        });
      }
    },
    [course, user?.active_channel]
  );

  useEffect(() => {
    if (workingEvent && user?.active_channel && !course) {
      (async () => {
        try {
          const [course] = await Promise.all([
            GetCourseForEvent(user.active_channel, workingEvent.uuid),
            GetChannelTracks(user.active_channel, undefined, 'courses'),
          ]);
          setCourse(course);
          setWorkingCertificate(course.certificate ?? createNewCertificate(user.active_channel, course.id, 1, user.id));
        } catch (e: unknown) {
          if (e instanceof FetchError && e.response && e.response.status === 404) {
            console.info('Course not found for event, creating new course');
          } else {
            toastTrigger(<div className="S1">Course failed to load</div>, {
              type: 'error',
              autoClose: 5000,
            });
            setCourseFetchError(true);
          }
        }
        setCourseLoading(false);
      })();
    }
  }, [workingEvent, user?.active_channel, course, user?.id]);

  const requirementName = useMemo(() => {
    return newRequirement.type === CourseRequirementType.watched_minute ? 'Watchtime' : 'Pulse check';
  }, [newRequirement.type]);

  const createRequirement = async () => {
    if (!user?.active_channel || !course || !newRequirement) return;

    if (sessionHasStarted(newRequirement)) {
      toastTrigger(
        <div className="S1">
          Cannot change course requirements
          <span>Updating course requirements not permitted for courses that are currently live</span>
        </div>,
        {
          type: 'warning',
          autoClose: 10000,
        }
      );
      return;
    }

    setSavingRequirement(true);

    try {
      const createdRequirement = await CreateCourseRequirement(user.active_channel, course.uuid, newRequirement);
      setCourse({
        ...course,
        requirements: [...(course.requirements || []), createdRequirement],
      });
      closeAddNewRequirementModal();
    } catch {
      toastTrigger(<div className="S1">Error adding course requirement</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }

    setSavingRequirement(false);
  };

  const canSaveNewRequirement = useMemo(
    () =>
      Boolean(
        newRequirement.type === CourseRequirementType.watched_minute
          ? !savingRequirement && newRequirement.session_id && newRequirement.required_amount
          : !savingRequirement &&
              newRequirement.session_id &&
              newRequirement.required_amount &&
              newRequirement.points &&
              newRequirement.required_amount <= newRequirement.points
      ),
    [newRequirement, savingRequirement]
  );

  const deleteRequirement = async () => {
    if (!user?.active_channel || !course || !requirementToDelete) return;

    const fullRequirementToDelete = course.requirements?.find(
      (requirement) => requirement.uuid === requirementToDelete
    );

    if (fullRequirementToDelete && sessionHasStarted(fullRequirementToDelete)) {
      toastTrigger(
        <div className="S1">
          Cannot change course requirements
          <span>Updating course requirements not permitted for courses that are currently live</span>
        </div>,
        {
          type: 'warning',
          autoClose: 10000,
        }
      );
      return;
    }

    setSavingRequirement(true);

    try {
      await DeleteCourseRequirement(user.active_channel, course.uuid, requirementToDelete);
      setCourse({
        ...course,
        requirements: course.requirements?.filter((requirement) => requirement.uuid !== requirementToDelete),
      });
      setRequirementToDelete(null);
    } catch {
      toastTrigger(<div className="S1">Error deleting course requirement</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }

    setSavingRequirement(false);
  };

  const updateRequirement = async () => {
    if (!user?.active_channel || !course || !requirementToUpdate) return;

    if (sessionHasStarted(newRequirement)) {
      toastTrigger(
        <div className="S1">
          Cannot change course requirements
          <span>Updating course requirements not permitted for courses that are currently live</span>
        </div>,
        {
          type: 'warning',
          autoClose: 10000,
        }
      );
      return;
    }

    setSavingRequirement(true);

    try {
      const updatedRequirement = await UpdateCourseRequirement(
        user.active_channel,
        course.uuid,
        requirementToUpdate,
        newRequirement
      );
      setCourse({
        ...course,
        requirements: course.requirements?.map((requirement) =>
          requirement.uuid === updatedRequirement.uuid ? updatedRequirement : requirement
        ),
      });
      closeAddNewRequirementModal();
    } catch {
      toastTrigger(<div className="S1">Error updating course requirement</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }

    setSavingRequirement(false);
  };

  const deleteCourse = async () => {
    if (!user?.active_channel || !course) return;

    try {
      await DeleteCourse(user.active_channel, course.uuid);
      setCourse(null);
      setWorkingCertificate(undefined);
      setActiveTab('Course');
      setShowDeleteCourseModal(false);
    } catch (e) {
      console.error(e);
      toastTrigger(<div className="S1">Error deleting course</div>, {
        type: 'error',
        autoClose: 5000,
      });
    }
  };

  const closeAddNewRequirementModal = () => {
    setShowAddNewRequirementModal(false);
    setNewRequirement(defaultNewRequirement);
    setRequirementToUpdate(null);
  };

  const getPulseCheckDurationDisplay = (duration: number) => {
    return duration < 60 ? `${duration}s` : `${duration / 60}m`;
  };

  const handleDesignEdit = (cert?: ICourseCertificate) => {
    if (cert && course) {
      setCourse({
        ...course,
        certificate: cert,
      });
      setWorkingCertificate(cert);
    }
    setEditDesignModalOpen(false);
  };

  const handleCertificateEmailSubjectChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (workingCertificate) {
      setWorkingCertificate({
        ...workingCertificate,
        email_subject: updateTranslateKey({
          translateString: workingCertificate.email_subject,
          input: e.target.value,
          baseLanguage: 'en', // TODO
          language: 'en',
        }),
      });
    }
  };

  const handleCertificateEmailBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (workingCertificate) {
      setWorkingCertificate({
        ...workingCertificate,
        email_body: updateTranslateKey({
          translateString: workingCertificate.email_body,
          input: e.target.value,
          baseLanguage: 'en', // TODO
          language: 'en',
        }),
      });
    }
  };

  const saveCertificate = async () => {
    if (workingCertificate && course) {
      try {
        let cert: ICourseCertificate;
        setSavingCertificate(true);

        if (!('uuid' in workingCertificate)) {
          // Create new certificate
          cert = await CreateCourseCertificate(course.channel, {
            ...workingCertificate,
            course_id: course.id,
          });
        } else {
          // Update existing certificate
          cert = await UpdateCourseCertificate(course.channel, workingCertificate.uuid, {
            ...workingCertificate,
            course_id: course.id,
          });
        }

        setWorkingCertificate(cert);
        setCourse({
          ...course,
          certificate: cert,
        });
      } catch (e) {
        console.error(e);
        toastTrigger(<div className="S1">Failed to save certificate</div>, {
          type: 'error',
          autoClose: 5000,
        });
      } finally {
        setSavingCertificate(false);
      }
    }
  };

  useEffect(() => {
    if (activeTab === 'Certificate') {
      setHasVisitedCertificateTab(true);
    }
  }, [activeTab]);

  if (!course && courseLoading) {
    return (
      <div className="flex" style={{ height: 'calc(100vh - 144px)' }}>
        <BrandedLoadingIndicator />
      </div>
    );
  }

  if (courseFetchError) {
    return (
      <Container className="flex ai-center jc-center" height="calc(100vh - 144px)">
        <Text className="H4">Course failed to load. Please try refreshing or contact support.</Text>
      </Container>
    );
  }

  if (!course && !courseLoading) {
    return <NoCourse setCourse={setCourse} />;
  }

  return (
    <>
      <div className="create-courses-container flex-column gap-24">
        <CourseHeader
          courseName={course?.name.base || ''}
          handleSave={handleSave}
          setShowDeleteCourseModal={setShowDeleteCourseModal}
        />
        <Tabs
          height="auto"
          selectedUnderlineColor={getThemeColorCSSVariable()}
          tabs={COURSE_TABS}
          containerClassName="fw-wrap jc-flex-start ai-center gap-20"
          onTabChange={(tab) => setActiveTab(tab.value)}
          renderTabContent={(tab) => {
            return (
              <div className="flex">
                <Text className="S1">{tab.label}</Text>
                {tab.value === 'Certificate' && !hasVisitedCertificateTab ? (
                  <span style={{ color: 'var(--semantic-red-500)' }}>*</span>
                ) : null}
              </div>
            );
          }}
        />
        <OptionalComponent display={activeTab === 'Course'}>
          <CourseMainPanel
            handleSave={handleSave}
            course={course}
            workingEvent={workingEvent}
            setRequirementToDelete={setRequirementToDelete}
            setRequirementToUpdate={setRequirementToUpdate}
            setNewRequirement={setNewRequirement}
            setShowAddNewRequirementModal={setShowAddNewRequirementModal}
            setShowAddRequirementPopover={setShowAddRequirementPopover}
            showAddRequirementPopover={showAddRequirementPopover}
          />
        </OptionalComponent>
        <OptionalComponent display={activeTab === 'Certificate'}>
          <CertificatePanel
            saveCertificate={saveCertificate}
            savingCertificate={savingCertificate}
            workingCertificate={workingCertificate}
            handleCertificateEmailSubjectChange={handleCertificateEmailSubjectChange}
            handleCertificateEmailBodyChange={handleCertificateEmailBodyChange}
            setEditDesignModalOpen={setEditDesignModalOpen}
            setShowEmailPreviewModal={setShowEmailPreviewModal}
            course={course}
          />
          <EditDesign onComplete={handleDesignEdit} open={editDesignModalOpen} course={course} />
        </OptionalComponent>
      </div>
      <CourseAddRequirementModal
        showAddNewRequirementModal={showAddNewRequirementModal}
        closeAddNewRequirementModal={closeAddNewRequirementModal}
        requirementName={requirementName}
        newRequirement={newRequirement}
        setNewRequirement={setNewRequirement}
        createRequirement={createRequirement}
        updateRequirement={updateRequirement}
        canSaveNewRequirement={canSaveNewRequirement}
        savingRequirement={savingRequirement}
        isEditorRole={isEditorRole}
        requirementToUpdate={requirementToUpdate}
        workingEvent={workingEvent}
        requirements={course?.requirements}
        getPulseCheckDurationDisplay={getPulseCheckDurationDisplay}
      />
      <CourseDeleteRequirementModal
        requirementToDelete={requirementToDelete}
        setRequirementToDelete={setRequirementToDelete}
        deleteRequirement={deleteRequirement}
        course={course}
      />
      <CourseDeleteModal
        showDeleteModal={showDeleteCourseModal}
        setShowDeleteModal={setShowDeleteCourseModal}
        courseName={course?.name.base || ''}
        deleteCourse={deleteCourse}
      />
      <CourseNoCertNavAlertModal
        showModal={showCertificateWarningModal}
        onContinue={() => {
          setShowCertificateWarningModal(false);
          blocker?.proceed?.();
        }}
        onCreate={() => {
          setShowCertificateWarningModal(false);
          setActiveTab('Certificate');
        }}
      />
      {workingCertificate && (
        <EmailPreview
          open={showEmailPreviewModal}
          onRequestClose={() => setShowEmailPreviewModal(false)}
          certificate={workingCertificate}
          bannerImage={getBannerImage(workingEvent)}
          channelIcon={channel?.logos.color}
          firstName={user?.profile.first_name ?? user?.email}
          lastName={user?.profile.last_name ?? undefined}
          courseName={course?.name.base || 'The Course'}
        />
      )}
    </>
  );
};

export default Courses;
